import $ from 'jquery'
import './autoplay.scss'
import { showImageOverlay, dismissOverlay } from './image_overlay'
import { updateUrl } from '~/common/helpers/update_url'

const removeAutoplayFromUrl = function () {
  // Remove autoplay parameter from URL so that page refreshes will restore position normally
  const newUrl = updateUrl(window.location.href, 'autoplay', null)
  if (newUrl) {
    window.history.replaceState({}, '', newUrl)
  }
}

export const setupAutoPlay = function (player, elem) {
  const $elem = $(elem)
  const urlSearchParams = new URLSearchParams(window.location.search)
  const params = Object.fromEntries(urlSearchParams.entries())

  // Either we have requested for this video to be autoplayed explicitly. In which case we'll attempt autoplay (if not in an iframe preview/editing).
  // If browsers complain, we'll mute and attempt autoplay again with sound muted (and showing a UI that says 'Click for sound').

  const autoplay =
    $elem.data('autoplay') &&
    !params['iframe_preview'] &&
    $('body').data('simplero-render-mode') != 'live_edit' &&
    !$('body').hasClass('js-no-videojs-autoplay')

  // Or we have a specific position provided for this asset in params.
  // In that case, we need to autoplay the video to show the proper poster (but with sound here).
  // Browsers can restrict auto-play. If we can't auto-play, we want to at least update the poster.
  // So mute the video (which allows for auto-play), play it, and immediately restore the sound (which will make the browser stop it again).
  // Doing this makes us show the proper poster for that point in time.
  const asset_id = $elem.data('asset-id')
  const restorePlaybackPosition = !!params['media_position_' + asset_id]

  if (autoplay || restorePlaybackPosition) {
    // Remove autoplay from URL once video starts playing
    if (autoplay) {
      player.one('playing', removeAutoplayFromUrl)
    }

    // Check if we have a custom image overlay for autoplay scenarios
    const imageOverlayUrl = $elem.data('image-overlay-url')

    if (autoplay && imageOverlayUrl) {
      // For custom image overlays with autoplay, always show the overlay and start muted
      let originalVolume = player.volume()
      player.volume(0)
      player.play()
      setupAutoplayOverlay(player, $elem, originalVolume)
    } else {
      // Normal autoplay behavior
      player.play().catch((e) => {
        let originalVolume = player.volume()
        player.volume(0)
        player.play()

        if (autoplay && !player.isAudio()) {
          // It's fine to keep autoplaying with sound muted here, but let's show a overlay to show 'Click for sound'...
          setupAutoplayOverlay(player, $elem, originalVolume)
        } else {
          // We don't want to autoplay with sound muted. So let's restore the sound. If it fails, it fails, atleast we got the correct poster...
          const restoreOriginalVolume = () => {
            player.volume(originalVolume)
            player.off('playing', restoreOriginalVolume)
          }
          player.on('playing', restoreOriginalVolume)
        }
      })
    }
  }
}

const setupAutoplayOverlay = function (player, elem, originalVolume) {
  // Try to setup image overlay first
  const imageOverlayShown = showImageOverlay(player, elem, {
    originalVolume,
    resetTime: true,
  })

  // If image overlay is not setup, show "Click to unmute" button
  if (!imageOverlayShown) setupClickForSound(player, elem, originalVolume)
}

// We use embedded SVG here becasue font-awesome is not supported in all places where we play
// videos (like legacy pages on sites)
const setupClickForSound = function (player, elem, originalVolume) {
  const container = elem.closest('.video-js')

  // Use default "Click to Unmute" overlay if no custom image
  container.addClass('vjs-click-for-sound-overlay-active')
  container.append(`
    <div class="video-js__click-for-sound-overlay">
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="25" viewBox="0 0 30 25" fill="none">
        <path d="M1.85979 0.285059C1.37381 -0.0981189 0.668206 -0.00933383 0.285028 0.476647C-0.0981494 0.962629 -0.00936434 1.66824 0.476617 2.05141L28.1402 23.7337C28.6261 24.1168 29.3318 24.028 29.7149 23.5421C30.0981 23.0561 30.0093 22.3505 29.5233 21.9673L24.5841 18.0935C26.0607 16.4953 26.9626 14.3598 26.9626 12.0094C26.9626 9.20095 25.6682 6.6916 23.6495 5.04674C23.1682 4.65422 22.4626 4.72898 22.0701 5.21029C21.6775 5.6916 21.7523 6.39721 22.2336 6.78973C23.7523 8.02338 24.7196 9.90188 24.7196 12.0094C24.7196 13.8365 23.9906 15.4907 22.8084 16.7056L21.028 15.3084C21.9159 14.4907 22.4766 13.3131 22.4766 12.0094C22.4766 10.6028 21.8271 9.35048 20.8224 8.52805C20.3411 8.13553 19.6355 8.21029 19.243 8.6916C18.8504 9.17291 18.9252 9.87852 19.4065 10.271C19.9112 10.6823 20.2336 11.3084 20.2336 12.0094C20.2336 12.7103 19.9112 13.3365 19.4065 13.7523C19.3458 13.7991 19.2944 13.8552 19.2476 13.9112L16.4953 11.757V3.0374C16.4953 2.44861 16.1495 1.9159 15.6121 1.67291C15.0747 1.42992 14.4439 1.52805 14.0047 1.92057L9.2897 6.11216L1.85979 0.285059ZM16.4953 17.4907L3.92054 7.58412C2.56073 7.86917 1.54204 9.07011 1.54204 10.514V13.5047C1.54204 15.1542 2.88316 16.4953 4.53269 16.4953H7.70092L14.0047 22.0981C14.4439 22.4907 15.0747 22.5841 15.6121 22.3458C16.1495 22.1075 16.4953 21.5701 16.4953 20.9813V17.4907Z" fill="#FCFCFD"/>
      </svg>
      ${elem.data('click-for-sound-label')}
    </div>
  `)

  container[0].addEventListener(
    'click',
    (e) => {
      if (!e.target.closest('.vjs-progress-control, .vjs-play-control')) {
        // If clicked anywhere other than the progress bar/play button, make sure we go back to 0:00
        player.currentTime(0)
      }
      if (!e.target.closest('.vjs-volume-panel')) {
        // If clicked anywhere other than the volume panel, make sure we go back to the original volume
        player.volume(originalVolume)
      }
      if (!e.target.closest('.vjs-play-control')) {
        // If clicked anywhere other than the play button, make sure we are in a playing state.
        setTimeout(() => player.play(), 0) // defer to make sure videojs doesn't pause right after we play (because this listener is fired during the capture phase)
      }
      // Mark overlay as dismissed for this session
      dismissOverlay(elem)

      container.find('.video-js__click-for-sound-overlay').remove()
      container.removeClass('vjs-click-for-sound-overlay-active')
    },
    { once: true, capture: true }
  )
}
